import { useEffect, useRef, useCallback } from 'react';
import { authService } from '../../services/authService';

const BASE_URL = 'https://development.m-alorfali.com';
const PING_INTERVAL_MS = 5 * 60 * 1000;     // 5 minutes
const RETRY_INTERVAL_MS = 3 * 60 * 1000;    // 3 minutes
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

async function sendPing(): Promise<boolean> {
  const token = authService.getToken();
  if (!token) return false;

  try {
    const response = await fetch(`${BASE_URL}/api/ping`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.status === 200;
  } catch {
    return false;
  }
}

export function usePingPong(isAuthenticated: boolean) {
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOnlineRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  const clearAllTimers = useCallback(() => {
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    pingTimerRef.current = null;
    retryTimerRef.current = null;
    inactivityTimerRef.current = null;
  }, []);

  const startRetry = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(async () => {
      const success = await sendPing();
      if (success) {
        isOnlineRef.current = true;
        startPingInterval(); // eslint-disable-line @typescript-eslint/no-use-before-define
      } else {
        startRetry();
      }
    }, RETRY_INTERVAL_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startPingInterval = useCallback(() => {
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(async () => {
      const success = await sendPing();
      if (!success) {
        isOnlineRef.current = false;
        clearInterval(pingTimerRef.current!);
        pingTimerRef.current = null;
        startRetry();
      }
    }, PING_INTERVAL_MS);
  }, [startRetry]);

  const goOnline = useCallback(async () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;

    const success = await sendPing();
    if (success) {
      isOnlineRef.current = true;
      startPingInterval();
    } else {
      isOnlineRef.current = false;
      startRetry();
    }
  }, [startPingInterval, startRetry]);

  const goOffline = useCallback(() => {
    isOnlineRef.current = false;
    clearAllTimers();
  }, [clearAllTimers]);

  // Visibility change handler
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();

        // Schedule going offline after INACTIVITY_LIMIT_MS
        inactivityTimerRef.current = setTimeout(() => {
          goOffline();
        }, INACTIVITY_LIMIT_MS);
      } else {
        // Tab became visible again
        const wasHiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;

        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
          inactivityTimerRef.current = null;
        }

        const hiddenDuration = wasHiddenAt ? Date.now() - wasHiddenAt : 0;
        const wasOffline = !isOnlineRef.current;

        // If was offline (either from inactivity or failed pings), restart
        if (wasOffline || hiddenDuration >= INACTIVITY_LIMIT_MS) {
          goOnline();
        }
        // Otherwise the ping interval is still running — nothing to do
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, goOnline, goOffline]);

  // Start/stop based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      goOnline();
    } else {
      clearAllTimers();
      isOnlineRef.current = false;
      hiddenAtRef.current = null;
    }

    return () => {
      clearAllTimers();
    };
  }, [isAuthenticated, goOnline, clearAllTimers]);
}
